// ================= INTI LALU LINTAS (murni, tanpa canvas) =================
// Simulasi kendaraan darat ala dunia nyata di atas grid jalan:
//  - Lalu lintas KIRI (Indonesia): tiap mobil berjalan di lajur kiri dari arah hadapnya.
//    Gang 1 tile = 2 lajur (kiri/kanan marka putih); jalan raya 2 tile = tiap tile
//    membawa SATU arah (2 lajur: luar dekat trotoar & dalam dekat marka kuning).
//  - Mobil mengikuti jalan tile demi tile, berbelok mulus di simpang (kurva bezier),
//    putar balik di jalan buntu, keluar/masuk kota lewat tepi peta.
//  - Ikut-antre: menjaga jarak & mengerem di belakang mobil di depan (time-headway).
//  - Simpang (tanpa lampu lalu lintas): aturan prioritas + klaim (reservasi) agar tidak
//    bertabrakan, dengan prioritas untuk yang paling lama menunggu (anti-deadlock).
import { hashN } from "./sky";

export const TRAFFIC = {
  laneOuter: 0.17,   // offset lajur luar (sisi trotoar) dari pusat tile, satuan tile
  laneInner: -0.25,  // lajur dalam (sisi marka kuning) — hanya di jalan raya 2 tile
  speed: { mobil: 0.42, berat: 0.30, motor: 0.50, sepeda: 0.20, darurat: 0.50 }, // tile/detik
  turnSpeed: 0.24,   // batas kecepatan saat berbelok / putar balik
  accel: 0.30, brake: 0.85, // tile/detik^2
  minGap: 0.20, headway: 0.9, lookahead: 1.8, // ikut-antre (jarak minimum, waktu antara, jangkauan)
  perTiles: 8, minCars: 2, maxCars: 36,        // kepadatan dasar: 1 mobil per N tile jalan
  poolFactor: 1.35,  // cadangan mobil (parkir) untuk jam sibuk = dasar × faktor
  fadeTiles: 0.8,    // jarak fade in/out saat masuk/keluar tepi peta
  waitPriority: 2.5, // detik menunggu sebelum mobil dapat prioritas di simpang tanpa lampu
  unitTile: 70 / 5720, // 1 unit kit kendaraan (T=70*zoom, S=T/80 px) dalam satuan tile
  // Profil jam sibuk: faktor kepadatan per jam kota (interpolasi linear)
  profile: [[0, 0.15], [4, 0.15], [6, 0.8], [7.5, 1.35], [9, 1.0], [12, 0.85], [15, 0.9], [17, 1.35], [18.5, 1.2], [20, 0.6], [22, 0.3], [24, 0.15]]
};
export function densityAt(hour) {
  const P = TRAFFIC.profile, h = ((hour % 24) + 24) % 24;
  for (let i = 1; i < P.length; i++) {
    const [h0, v0] = P[i - 1], [h1, v1] = P[i];
    if (h <= h1) return v0 + (v1 - v0) * ((h - h0) / (h1 - h0));
  }
  return P[P.length - 1][1];
}
// Label fase hari untuk HUD jam kota
export function phaseLabel(hour) {
  const h = ((hour % 24) + 24) % 24;
  if (h < 4.5) return "Dini hari";
  if (h < 6) return "Subuh";
  if (h < 9.5) return "Jam sibuk pagi";
  if (h < 15.5) return "Siang";
  if (h < 19) return "Jam sibuk sore";
  if (h < 22) return "Malam";
  return "Tengah malam";
}

export const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1]];
export const tkey = (x, y) => x + "," + y;
// Kiri dari arah hadap d di koordinat grid (lalu lintas kiri). Diverifikasi terhadap layar:
// hadap +x (SE layar) → kiri = -y (NE layar); hadap +y (SW layar) → kiri = +x (SE layar).
export const left = (d) => [d[1], -d[0]];
export const neg = (d) => [-d[0], -d[1]];
export const eq = (a, b) => a[0] === b[0] && a[1] === b[1];
export const axisOf = (d) => (d[0] !== 0 ? "x" : "y");
export const dirOf = (d) => (d[0] > 0 ? "SE" : d[0] < 0 ? "NW" : d[1] > 0 ? "SW" : "NE");
// Manuver relatif arah masuk: S lurus, L kiri, R kanan, U putar balik
export function turnOf(dIn, dOut) {
  if (eq(dIn, dOut)) return "S";
  if (eq(dOut, left(dIn))) return "L";
  if (eq(dOut, neg(dIn))) return "U";
  return "R";
}
const add = (p, d, k) => [p[0] + d[0] * k, p[1] + d[1] * k];

// ---- Bezier linear/kuadratik/kubik + tabel panjang busur (kecepatan seragam) ----
export function bezAt(p, u) {
  const iu = 1 - u;
  if (p.length === 2) return [p[0][0] + (p[1][0] - p[0][0]) * u, p[0][1] + (p[1][1] - p[0][1]) * u];
  if (p.length === 3) return [iu * iu * p[0][0] + 2 * iu * u * p[1][0] + u * u * p[2][0], iu * iu * p[0][1] + 2 * iu * u * p[1][1] + u * u * p[2][1]];
  const a = iu * iu * iu, b = 3 * iu * iu * u, c = 3 * iu * u * u, e = u * u * u;
  return [a * p[0][0] + b * p[1][0] + c * p[2][0] + e * p[3][0], a * p[0][1] + b * p[1][1] + c * p[2][1] + e * p[3][1]];
}
function makeSeg(p, kind, dIn, dOut, endTile, cluster) {
  const N = p.length === 2 ? 1 : 16;
  const cum = [0];
  let prev = bezAt(p, 0);
  for (let i = 1; i <= N; i++) {
    const q = bezAt(p, i / N);
    cum.push(cum[i - 1] + Math.hypot(q[0] - prev[0], q[1] - prev[1]));
    prev = q;
  }
  return { p, kind, dIn, dOut, endTile, cluster, len: cum[N], cum, N, nextCluster: null };
}
export function segPose(seg, s) {
  let u;
  if (seg.N === 1) u = seg.len ? s / seg.len : 1;
  else {
    let i = 1;
    while (i < seg.N && seg.cum[i] < s) i++;
    const a = seg.cum[i - 1], b = seg.cum[i];
    u = (i - 1 + (b > a ? (s - a) / (b - a) : 0)) / seg.N;
  }
  u = Math.max(0, Math.min(1, u));
  const pos = bezAt(seg.p, u);
  const q = bezAt(seg.p, Math.min(1, u + 0.02)), r = bezAt(seg.p, Math.max(0, u - 0.02));
  let tx = q[0] - r[0], ty = q[1] - r[1];
  const n = Math.hypot(tx, ty) || 1;
  return { x: pos[0], y: pos[1], tx: tx / n, ty: ty / n, u };
}

// ---- Jaringan jalan: klasifikasi tile, klaster simpang, lengan masuk/keluar ----
export function buildNetwork(roads, G) {
  const set = new Set(roads || []);
  const isRoad = (x, y) => set.has(tkey(x, y));
  const inGrid = (x, y) => x >= 0 && y >= 0 && x < G && y < G;
  // Di luar peta dianggap "jalan berlanjut" agar ruas yang menembus tepi tetap LURUS
  // (mobil keluar/masuk kota), tapi hanya untuk kelanjutan searah sumbu — bukan lateral.
  const roadOrOut = (x, y) => !inGrid(x, y) || isRoad(x, y);
  const parX = (x, y) => isRoad(x, y) && roadOrOut(x + 1, y) && roadOrOut(x - 1, y);
  const parY = (x, y) => isRoad(x, y) && roadOrOut(x, y + 1) && roadOrOut(x, y - 1);
  const info = new Map();
  for (const k of set) {
    const [x, y] = k.split(",").map(Number);
    const n = DIRS.filter(d => isRoad(x + d[0], y + d[1])).length;
    const px = roadOrOut(x + 1, y) && roadOrOut(x - 1, y), py = roadOrOut(x, y + 1) && roadOrOut(x, y - 1);
    // tile LURUS sumbu x: tetangga x di kedua sisi, dan tetangga lateral (jika ada)
    // adalah pita jalan sejajar (jalan raya 2 tile), bukan cabang.
    const sx = px && (!isRoad(x, y + 1) || parX(x, y + 1)) && (!isRoad(x, y - 1) || parX(x, y - 1));
    const sy = py && (!isRoad(x + 1, y) || parY(x + 1, y)) && (!isRoad(x - 1, y) || parY(x - 1, y));
    const axis = sx && !sy ? "x" : sy && !sx ? "y" : null;
    info.set(k, { x, y, n, axis, cluster: -1 });
  }
  const parAlong = (x, y, d) => isRoad(x, y) && roadOrOut(x + d[0], y + d[1]) && roadOrOut(x - d[0], y - d[1]);
  // Sisi benar untuk lalu lintas kiri: SALAH bila sisi KIRI tile punya pita sejajar
  // (tile ini bagian kanan jalan raya → milik arah berlawanan) dan sisi kanan tidak.
  const sideOK = (x, y, d) => {
    const l = left(d);
    const pl = parAlong(x + l[0], y + l[1], d), pr = parAlong(x - l[0], y - l[1], d);
    return !(pl && !pr);
  };
  // lajur dalam tersedia bila sisi kanan tile adalah pita sejajar (tanpa trotoar)
  const innerOK = (x, y, d) => { const l = left(d); return parAlong(x - l[0], y - l[1], d); };
  const okEnter = (x, y, d) => {
    if (!inGrid(x, y) || !isRoad(x, y)) return false;
    const i = info.get(tkey(x, y));
    return (i.axis === null || i.axis === axisOf(d)) && sideOK(x, y, d);
  };
  // klaster = komponen 4-terhubung dari tile simpul (bukan lurus): simpang, tikungan, buntu
  const clusters = [];
  for (const [, i] of info) {
    if (i.axis !== null || i.cluster >= 0 || i.n === 0) continue;
    const id = clusters.length, tiles = [], stack = [i];
    i.cluster = id;
    while (stack.length) {
      const c = stack.pop();
      tiles.push(c);
      for (const d of DIRS) {
        const j = info.get(tkey(c.x + d[0], c.y + d[1]));
        if (j && j.axis === null && j.cluster < 0 && j.n > 0) { j.cluster = id; stack.push(j); }
      }
    }
    const inCl = (x, y) => { const j = info.get(tkey(x, y)); return !!j && j.cluster === id; };
    const exits = [], approaches = [];
    for (const c of tiles) {
      for (const d of DIRS) {
        const nx = c.x + d[0], ny = c.y + d[1];
        if (isRoad(nx, ny) && !inCl(nx, ny) && okEnter(nx, ny, d)) exits.push({ cx: c.x, cy: c.y, d, nx, ny });
        const ax = c.x - d[0], ay = c.y - d[1]; // tile pendekat A → masuk ke c dengan arah d
        if (isRoad(ax, ay) && !inCl(ax, ay)) {
          const ia = info.get(tkey(ax, ay));
          if ((ia.axis === null || ia.axis === axisOf(d)) && sideOK(ax, ay, d)) approaches.push({ ax, ay, d, cx: c.x, cy: c.y });
        }
      }
    }
    clusters.push({ id, tiles, exits, approaches, claims: [], block: null });
  }
  // titik masuk/keluar kota: tile lurus di batas grid yang terbuka keluar peta
  const entries = [];
  for (const [, i] of info) {
    if (i.axis === null) continue;
    for (const d of DIRS) {
      if (axisOf(d) !== i.axis) continue;
      if (!inGrid(i.x - d[0], i.y - d[1]) && sideOK(i.x, i.y, d)) entries.push({ x: i.x, y: i.y, d });
    }
  }
  const straights = [...info.values()].filter(i => i.axis !== null);
  return { set, isRoad, inGrid, info, clusters, sideOK, innerOK, okEnter, entries, straights, G };
}

// Dua manuver bersilangan? (a, b: {dIn, dOut})
export function conflict(a, b) {
  const ta = turnOf(a.dIn, a.dOut), tb = turnOf(b.dIn, b.dOut);
  if (eq(a.dIn, b.dIn)) return ta !== tb && tb !== "S"; // satu lengan: pengikut menunggu bila pemimpin (b) berbelok ke arah lain
  if (eq(a.dIn, neg(b.dIn))) { // berpapasan: lurus/kiri tidak bersilangan; kanan & putar balik bersilangan
    return !((ta === "S" || ta === "L") && (tb === "S" || tb === "L"));
  }
  return !(ta === "L" && tb === "L"); // tegak lurus: hanya dua-duanya belok kiri yang aman
}

// ---- Kelas kecepatan kendaraan dari bid/nama aset ----
export function speedClass(def) {
  const s = ((def?.bid || "") + " " + (def?.name || "")).toLowerCase();
  if (/sepeda(?!_motor)|bicycle/.test(s) && !/motor/.test(s)) return "sepeda";
  if (/roda_dua|motor/.test(s)) return "motor";
  if (/polisi|ambulans|sar\b|pemadam/.test(s)) return "darurat";
  if (/bus|truk|molen|sampah|kargo/.test(s)) return "berat";
  return "mobil";
}
// Panjang kendaraan (tile) dari `L=` di paintCode (1 unit kit = TRAFFIC.unitTile tile)
export function vehicleLength(def) {
  const code = def?.paintCode || "";
  const body = code.includes("function propeller") ? code.split("function propeller")[1] : code;
  const m = body.match(/\bL\s*=\s*([\d.]+)/);
  const cls = speedClass(def);
  const L = m ? parseFloat(m[1]) : (cls === "berat" ? 54 : cls === "sepeda" ? 20 : cls === "motor" ? 22 : 40);
  return Math.max(0.22, L * TRAFFIC.unitTile);
}

// ================= SIMULASI =================
export function createTrafficCore(G) {
  let net = null;
  let cars = [];
  let clock = 0;
  let base = 0, target = 0, lastBalance = -1e9;
  const activeCount = () => cars.filter(c => !c.parked).length;

  const laneOff = (x, y, d, lane) => (lane === 1 && net.innerOK(x, y, d) ? TRAFFIC.laneInner : TRAFFIC.laneOuter);
  const lanePoint = (x, y, d, lane, k) => { // titik lajur di tile (x,y), k=-0.5 bibir masuk / +0.5 bibir keluar
    const lo = laneOff(x, y, d, lane), l = left(d);
    return [x + 0.5 + d[0] * k + l[0] * lo, y + 0.5 + d[1] * k + l[1] * lo];
  };
  const nearCar = (x, y, r) => cars.some(c => !c.hidden && Math.hypot(c.gx - x, c.gy - y) < r);

  function setNextCluster(seg) {
    const d = seg.dOut, k = net.info.get(tkey(seg.endTile[0] + d[0], seg.endTile[1] + d[1]));
    seg.nextCluster = k && k.cluster >= 0 ? net.clusters[k.cluster] : null;
  }
  function pose(c) {
    const ps = segPose(c.seg, c.s);
    c.gx = ps.x; c.gy = ps.y; c.tx = ps.tx; c.ty = ps.ty;
    const turning = c.seg.kind === "turn" || c.seg.kind === "uturn";
    c.dir = dirOf(turning && ps.u >= 0.5 ? c.seg.dOut : c.seg.dIn);
    const k = c.seg.kind;
    c.alpha = k === "exit" ? Math.max(0, 1 - ps.u) : k === "enter" ? Math.min(1, c.s / TRAFFIC.fadeTiles) : c.fade > 0 ? 1 - c.fade : 1;
    if (c.leaving) c.alpha *= Math.max(0, 1 - c.leaveT);
  }

  // Tempatkan mobil di tile lurus acak (spawn awal / respawn tanpa tepi)
  function placeRandom(c, seed) {
    for (let k = 0; k < 60; k++) {
      const i = net.straights[Math.floor(hashN(seed + k * 3.3) * net.straights.length) % net.straights.length];
      const ds = DIRS.filter(d => axisOf(d) === i.axis && net.sideOK(i.x, i.y, d));
      if (!ds.length) continue;
      const d = ds[Math.floor(hashN(seed + k * 7.7) * ds.length) % ds.length];
      const P0 = lanePoint(i.x, i.y, d, c.lane, -0.5), P1 = lanePoint(i.x, i.y, d, c.lane, 0.5);
      if (nearCar((P0[0] + P1[0]) / 2, (P0[1] + P1[1]) / 2, 1.3)) continue;
      c.seg = makeSeg([P0, P1], "straight", d, d, [i.x, i.y], null);
      setNextCluster(c.seg);
      c.s = hashN(seed + 0.5) * 0.4;
      c.v = c.vmax * 0.5; c.permit = false; c.plan = null; c.wait = 0; c.hidden = false; c.fade = 1;
      pose(c);
      return true;
    }
    return false;
  }
  // Masuk dari tepi peta (fade in sambil melaju)
  function placeAtEntry(c, seed) {
    const e = net.entries[Math.floor(hashN(seed) * net.entries.length) % net.entries.length];
    const P1 = lanePoint(e.x, e.y, e.d, c.lane, 0.5);
    const P0 = add(lanePoint(e.x, e.y, e.d, c.lane, -0.5), e.d, -TRAFFIC.fadeTiles);
    if (nearCar(P1[0], P1[1], 1.6)) return false;
    c.seg = makeSeg([P0, P1], "enter", e.d, e.d, [e.x, e.y], null);
    setNextCluster(c.seg);
    c.s = 0; c.v = c.vmax * 0.7; c.permit = false; c.plan = null; c.wait = 0; c.hidden = false; c.fade = 0;
    pose(c);
    return true;
  }
  function respawn(c) {
    const seed = c.seed * 17.3 + c.trips * 3.1 + clock * 0.01;
    const ok = net.entries.length ? placeAtEntry(c, seed) : placeRandom(c, seed);
    if (!ok) c.timer = 0.6; // coba lagi sebentar lagi
    else { c.parked = false; c.leaving = false; c.leaveT = 0; }
  }
  // Mobil hilang dari peta: bila kota sedang lebih padat dari target jam ini → parkir
  // (tidak kembali), selain itu kembali setelah jeda singkat.
  function hide(c) {
    c.hidden = true; c.trips++; c.leaving = false; c.leaveT = 0;
    if (activeCount() > target) { c.parked = true; c.timer = Infinity; }
    else c.timer = 1 + hashN(c.seed + c.trips) * 2.5;
  }

  function build(state, defs, hour = 12) {
    net = buildNetwork(state.roads, G);
    cars = [];
    const vehicleDefs = (defs || []).filter(d => d.category === "Kendaraan" && d.paintCode &&
      !/pesawat|heli|helicopter|airplane|speedboat|boat|ship|kapal|perahu|sampan|balon|balloon/i.test((d.bid || "") + " " + (d.name || "")));
    if (!net.straights.length) return;
    base = Math.min(TRAFFIC.maxCars, Math.max(TRAFFIC.minCars, Math.round(net.set.size / TRAFFIC.perTiles)));
    const n = Math.round(base * TRAFFIC.poolFactor);
    target = Math.max(1, Math.min(n, Math.round(base * densityAt(hour))));
    lastBalance = -1e9;
    for (let i = 0; i < n; i++) {
      const def = vehicleDefs.length ? vehicleDefs[Math.floor(hashN(200 + i * 13) * vehicleDefs.length) % vehicleDefs.length] : null;
      const cls = speedClass(def);
      const c = {
        id: i, seed: i, def, cls, trips: 0,
        vmax: TRAFFIC.speed[cls] * (0.9 + hashN(400 + i) * 0.2),
        half: vehicleLength(def) / 2,
        lane: cls === "sepeda" ? 0 : (hashN(500 + i) < 0.4 ? 1 : 0),
        s: 0, v: 0, seg: null, permit: false, plan: null, wait: 0, hidden: false, timer: 0, fade: 0, brake: false,
        gx: 0, gy: 0, tx: 1, ty: 0, dir: "SE", alpha: 1, parked: false, leaving: false, leaveT: 0
      };
      if (i >= target) { c.hidden = true; c.parked = true; c.timer = Infinity; cars.push(c); continue; } // cadangan jam sibuk
      if (placeRandom(c, 100 + i * 17)) { c.fade = 0; cars.push(c); }
    }
  }

  // Jam sibuk: sesuaikan jumlah mobil aktif ke target jam kota — kelebihan memudar
  // sambil melaju lalu parkir; kekurangan dimunculkan dari tepi kota / titik acak.
  function balance(hour, t) {
    if (t - lastBalance < 1.5) return;
    lastBalance = t;
    target = Math.max(1, Math.min(cars.length, Math.round(base * densityAt(hour))));
    const active = cars.filter(c => !c.parked);
    if (active.length > target) {
      const cand = active.filter(c => !c.hidden && !c.leaving);
      if (cand.length) cand[Math.floor(hashN(t) * cand.length) % cand.length].leaving = true;
    } else if (active.length < target) {
      const c = cars.find(x => x.parked);
      if (c) { c.timer = 0.2; c.parked = false; } // keluar dari parkir → respawn
    }
  }

  // Pilih lengan keluar klaster (bobot: lurus > kiri > kanan >> putar balik)
  function choosePlan(c, cl, entry, dIn) {
    const back = (e) => e.cx === entry[0] && e.cy === entry[1] && eq(e.d, neg(dIn));
    let ex = cl.exits.filter(e => !back(e));
    const nonU = ex.filter(e => !eq(e.d, neg(dIn)));
    if (nonU.length) ex = nonU;
    if (!ex.length) ex = cl.exits.filter(back);
    if (!ex.length) return null;
    const W = { S: 3, L: 1.5, R: 1, U: 0.3 };
    const ws = ex.map(e => W[turnOf(dIn, e.d)]);
    let r = hashN(c.seed * 13.1 + entry[0] * 3.7 + entry[1] * 7.9 + c.trips * 5.3) * ws.reduce((a, b) => a + b, 0);
    let pick = ex[ex.length - 1];
    for (let i = 0; i < ex.length; i++) { r -= ws[i]; if (r <= 0) { pick = ex[i]; break; } }
    return { cluster: cl, exit: pick, turn: turnOf(dIn, pick.d) };
  }

  // Segmen berikutnya setelah c.seg habis. false → mobil tidak punya jalan (respawn).
  function advance(c) {
    const seg = c.seg, d = seg.dOut;
    const nx = seg.endTile[0] + d[0], ny = seg.endTile[1] + d[1];
    const P0 = bezAt(seg.p, 1);
    c.permit = false;
    if (!net.inGrid(nx, ny)) { // keluar kota lewat tepi peta
      c.seg = makeSeg([P0, add(P0, d, TRAFFIC.fadeTiles)], "exit", d, d, [nx, ny], null);
      c.plan = null;
      return true;
    }
    if (!net.isRoad(nx, ny)) return false;
    const inf = net.info.get(tkey(nx, ny));
    if (inf.cluster >= 0) {
      const cl = net.clusters[inf.cluster];
      const plan = c.plan && c.plan.cluster === cl ? c.plan : choosePlan(c, cl, [nx, ny], d);
      c.plan = null;
      if (!plan) return false;
      const e = plan.exit;
      const X = lanePoint(e.cx, e.cy, e.d, c.lane, 0.5);
      let p, kind;
      if (plan.turn === "S") { p = [P0, X]; kind = "straight"; }
      else if (plan.turn === "U") {
        const k = 0.8 + 0.4 * Math.hypot(X[0] - P0[0], X[1] - P0[1]);
        p = [P0, add(P0, d, k), add(X, d, k), X]; kind = "uturn";
      } else {
        const dot = (X[0] - P0[0]) * d[0] + (X[1] - P0[1]) * d[1];
        p = [P0, add(P0, d, Math.max(0.05, dot)), X]; kind = "turn";
      }
      c.seg = makeSeg(p, kind, d, e.d, [e.cx, e.cy], cl);
      c.trips++;
    } else {
      if (inf.axis !== axisOf(d)) return false; // masuk tile lurus dari samping (konfigurasi tak lazim)
      c.seg = makeSeg([P0, lanePoint(nx, ny, d, c.lane, 0.5)], "straight", d, d, [nx, ny], null);
    }
    setNextCluster(c.seg);
    return true;
  }

  // Mobil terdekat di depan pada koridor lajur yang sama → jarak bumper-ke-bumper.
  // Sumbu acuan = arah KELUAR segmen (saat berbelok, tangen sesaat miring dan bisa salah
  // "melihat" mobil di lajur berlawanan yang sedang menunggu di garis henti).
  function gapAhead(c) {
    let gap = Infinity;
    const turning = c.seg.kind === "turn" || c.seg.kind === "uturn";
    const ax = turning ? c.seg.dOut[0] : c.tx, ay = turning ? c.seg.dOut[1] : c.ty;
    for (const o of cars) {
      if (o === c || o.hidden) continue;
      const rx = o.gx - c.gx, ry = o.gy - c.gy;
      const proj = rx * ax + ry * ay;
      if (proj <= 0 || proj > TRAFFIC.lookahead) continue;
      if (Math.abs(-rx * ay + ry * ax) > 0.26) continue;   // bukan lajur yang sama
      const oturn = o.seg.kind === "turn" || o.seg.kind === "uturn";
      const ox = oturn ? o.seg.dOut[0] : o.tx, oy = oturn ? o.seg.dOut[1] : o.ty;
      if (ox * ax + oy * ay < -0.5) continue;              // berlawanan arah (lajur sebelah)
      const g = proj - c.half - o.half;
      if (g < gap) gap = g;
    }
    return gap;
  }

  function tryPermit(c, t) {
    const seg = c.seg, cl = seg.nextCluster, dIn = seg.dOut;
    const entry = [seg.endTile[0] + dIn[0], seg.endTile[1] + dIn[1]];
    if (!c.plan || c.plan.cluster !== cl) c.plan = choosePlan(c, cl, entry, dIn);
    if (!c.plan) { c.permit = true; return; }
    const intent = { car: c, dIn, dOut: c.plan.exit.d };
    // Mobil yang sudah lama menunggu (mis. belok kanan menunggu celah) mendapat prioritas:
    // pendatang lain yang bersilangan dengannya ikut menahan diri sampai ia lewat.
    const blockFor = () => { if (c.wait > TRAFFIC.waitPriority && !cl.block) cl.block = intent; };
    if (cl.block && cl.block.car !== c && conflict(intent, cl.block)) return;         // hormati mobil berprioritas
    for (const k of cl.claims) if (k.car !== c && conflict(intent, k)) { blockFor(); return; }
    c.permit = true; c.wait = 0;
    cl.claims.push(intent);
  }

  function stepCar(c, dt, t) {
    if (c.hidden) { c.timer -= dt; if (c.timer <= 0) respawn(c); return; }
    if (c.fade > 0) c.fade = Math.max(0, c.fade - dt / 0.7);
    if (c.leaving) { c.leaveT += dt / 1.2; if (c.leaveT >= 1) { c.hidden = true; c.parked = true; c.timer = Infinity; c.leaving = false; c.leaveT = 0; return; } }
    const seg = c.seg;
    const turning = seg.kind === "turn" || seg.kind === "uturn";
    let vT = turning ? Math.min(c.vmax, TRAFFIC.turnSpeed) : c.vmax;
    const remain = seg.len - c.s;
    // perlambat menjelang belokan/simpang berikutnya
    const nextTurn = c.plan ? c.plan.turn !== "S" : !!seg.nextCluster;
    if (nextTurn) vT = Math.min(vT, Math.sqrt(TRAFFIC.turnSpeed ** 2 + 2 * TRAFFIC.brake * Math.max(0, remain)));
    // garis henti sebelum simpang (izin masuk lewat klaim / lampu)
    let stopAt = Infinity;
    if (seg.nextCluster && !c.permit) {
      stopAt = seg.len - c.half - 0.05;
      const dStop = stopAt - c.s;
      if (dStop < 1.0) tryPermit(c, t);
      if (!c.permit) {
        vT = Math.min(vT, Math.sqrt(2 * TRAFFIC.brake * Math.max(0, dStop)));
        if (dStop < 0.03) { vT = 0; c.wait += dt; }
      } else stopAt = Infinity;
    }
    // ikut-antre di belakang mobil di depan
    const gap = gapAhead(c);
    if (gap < Infinity) vT = Math.min(vT, Math.max(0, (gap - TRAFFIC.minGap) / TRAFFIC.headway));
    // "Jangan blokir simpang": sudah dapat izin tapi tertahan antrean di depan >2 dtk →
    // lepaskan izin (klaimnya hilang) agar arus lain bisa lewat; minta lagi nanti.
    if (c.permit && seg.nextCluster && vT < 0.02) {
      c.permitWait = (c.permitWait || 0) + dt;
      if (c.permitWait > 2) { c.permit = false; c.permitWait = 0; }
    } else c.permitWait = 0;
    const dv = vT - c.v;
    c.brake = dv < -0.05 || (vT === 0 && c.v < 0.03);
    c.v = Math.max(0, c.v + Math.max(-TRAFFIC.brake * dt, Math.min(TRAFFIC.accel * dt, dv)));
    c.s += c.v * dt;
    if (stopAt < Infinity && c.s > stopAt) { c.s = stopAt; c.v = 0; }
    if (c.s >= seg.len) {
      if (seg.kind === "exit") { hide(c); return; }
      const over = c.s - seg.len;
      if (!advance(c)) { hide(c); return; }
      c.s = Math.min(over, c.seg.len);
    }
    pose(c);
  }

  function update(dt, t, hour = 12) {
    clock = t;
    if (!net || !cars.length) return;
    balance(hour, t);
    for (const cl of net.clusters) { cl.claims.length = 0; cl.block = null; }
    for (const c of cars) {
      if (c.hidden) continue;
      if (c.seg.cluster) c.seg.cluster.claims.push({ car: c, dIn: c.seg.dIn, dOut: c.seg.dOut });
      else if (c.permit && c.plan && c.seg.nextCluster === c.plan.cluster) c.plan.cluster.claims.push({ car: c, dIn: c.seg.dOut, dOut: c.plan.exit.d });
    }
    // yang paling lama menunggu diproses lebih dulu (adil & anti-deadlock)
    const order = cars.slice().sort((a, b) => b.wait - a.wait);
    for (const c of order) stepCar(c, dt, t);
  }

  return { build, update, get cars() { return cars; }, get net() { return net; } };
}
